import HeaderUI, { Props } from '@interflora/ui-components/build/components/Header/Header'
import {
  useSearchNBHitsContext,
  useSearchState,
  useSearchUpdate,
} from '@interflora/ui-components/build/components/SearchContext/SearchContext'
import { SiteContext } from '@interflora/ui-components/build/utils/common'
import SearchNBHitsStats from '@interflora/ui-components/build/utils/SearchNBHitsStats'
import { algoliasearch } from 'algoliasearch'
import { useAnalytics } from 'hooks/useAnalytics'
import { signOut } from 'lib/apolloClient'
import { useRouter } from 'next/router'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Configure, Index, InstantSearch, InstantSearchSSRProvider, useInstantSearch } from 'react-instantsearch'

/**
 * Manual Editor redirects — handled via useQueryRules (queryRuleCustomData widget equivalent).
 * Reads the `userData` array returned by matching Manual Rules and finds the redirect value.
 * Supports plain-string format: { redirect: "/path" }
 * and object format:            { redirect: { url: "/path", force: true } }
 */
function getManualRuleRedirectUrl(items?: any[]): string | null {
  const match = items?.find((item) => item?.redirect)
  const raw = match?.redirect
  return typeof raw === 'string' ? raw : raw?.url ?? null
}

/**
 * Visual Editor redirects — handled as a custom InstantSearch widget via useInstantSearch.
 * Visual Editor rules set renderingContent.redirect.url instead of userData.
 */
function getVisualEditorRedirectUrl(renderingContent: any): string | null {
  return renderingContent?.redirect?.url ?? null
}

const envPrefix = process.env.NEXT_PUBLIC_ALGOLIA_ENV_PREFIX
const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID ?? '',
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY ?? ''
)

const Header = (props: Props) => {
  const { shorthand, getConsignmentCount } = useContext(SiteContext)
  const analytics = useAnalytics()
  const [showLoader, setShowLoader] = useState(false)
  const [pendingSearchQuery, setPendingSearchQuery] = useState<string | null>(null)
  const lastRedirectedQuery = useRef<string | null>(null)
  // default to 'uk' if undefined
  const siteShorthand = shorthand || 'uk'
  const productIndex = `${envPrefix}_product_${siteShorthand}`
  const categoryIndex = `${envPrefix}_category_${siteShorthand}`
  const inspirationIndex = `${envPrefix}_inspiration_${siteShorthand}`

  const handleSignOut = async () => {
    await signOut()
    analytics.signOut()
    window.location.assign('/')
  }
  const router = useRouter()

  const handleRouteChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      getConsignmentCount?.refetch()
    }
  }, [getConsignmentCount])

  useEffect(() => {
    document?.addEventListener('visibilitychange', handleRouteChange)
    return () => {
      document?.removeEventListener('visibilitychange', handleRouteChange)
    }
  }, [handleRouteChange])

  const handleSuggestedSearchOrPreviousSearch = async (previousSearchValues: string) => {
    const algoliaSearchResults = await searchClient.searchSingleIndex({
      indexName: productIndex,
      searchParams: {
        query: previousSearchValues,
        distinct: true,
        hitsPerPage: 6,
        clickAnalytics: true,
        ruleContexts: ['search'],
        analyticsTags: ['search'],
        filters: 'NOT isAlternativeProduct:true',
      },
    })

    // Manual Editor redirect — queryRuleCustomData widget equivalent
    const manualRuleRedirectUrl = getManualRuleRedirectUrl(algoliaSearchResults?.userData)
    // Visual Editor redirect — custom widget via renderingContent
    const visualEditorRedirectUrl = getVisualEditorRedirectUrl(algoliaSearchResults?.renderingContent)
    const redirectUrl = manualRuleRedirectUrl || visualEditorRedirectUrl

    if (redirectUrl) {
      analytics.searchRedirect(redirectUrl)
    }
    const finalUrl = redirectUrl || `/search?query=${encodeURIComponent(previousSearchValues)}`
    router.push(finalUrl)
  }
  const searchTermAnalytics = (searchInputValue: string) => {
    analytics.searchTerm(searchInputValue)
  }
  const noSearchOverlayResultAnalytics = (message: string) => {
    analytics.searchOverlayNoResults(message)
  }

  // Wrap the Algolia InstantSearch with an empty SSR Provider to allow static HTML generation for the header
  return (
    <InstantSearchSSRProvider initialResults={{}}>
      <InstantSearch
        searchClient={searchClient}
        indexName={productIndex}
        future={{ preserveSharedStateOnUnmount: true }}
        insights={true}
        // Prevent additional searches until query length is 3 characters - still fires empty search
        // Prevent all searches until query length is 3 characters
        onStateChange={({ uiState, setUiState }) => {
          if ((uiState[productIndex]?.query?.length ?? 0) > 1) {
            setUiState(uiState)
          }
        }}
      >
        <SearchNBHitsStats />
        <Index indexName={categoryIndex}>
          <SearchNBHitsStats />
        </Index>
        <Index indexName={inspirationIndex}>
          <SearchNBHitsStats />
        </Index>
        <SearchRedirect
          algoliaIndexes={[productIndex, inspirationIndex]}
          setShowLoader={setShowLoader}
          pendingSearchQuery={pendingSearchQuery}
          setPendingSearchQuery={setPendingSearchQuery}
          lastRedirectedQuery={lastRedirectedQuery}
        />
        <Configure
          hitsPerPage={6}
          distinct
          clickAnalytics
          ruleContexts={['search']}
          analyticsTags={['search']}
          filters="NOT isAlternativeProduct:true"
        />
        <HeaderUI
          {...props}
          signOut={handleSignOut}
          handleSuggestedSearchOrPreviousSearch={handleSuggestedSearchOrPreviousSearch}
          searchTermAnalytics={searchTermAnalytics}
          noSearchOverlayResultAnalytics={noSearchOverlayResultAnalytics}
          sendMenuDrillDownAnalytics={(clickText) => analytics.menuDrillDownClick(clickText)}
          sendMenuLinkClickAnalytics={(menuText) => analytics.menulinkClick(menuText)}
          setShowLoader={setShowLoader}
          showLoader={showLoader}
          setPendingSearchQuery={setPendingSearchQuery}
        />
      </InstantSearch>
    </InstantSearchSSRProvider>
  )
}

const SearchRedirect = ({
  algoliaIndexes,
  setShowLoader,
  pendingSearchQuery,
  setPendingSearchQuery,
  lastRedirectedQuery,
}: {
  algoliaIndexes: string[]
  setShowLoader: (loader: boolean) => void
  pendingSearchQuery: string | null
  setPendingSearchQuery: (query: string | null) => void
  lastRedirectedQuery: React.MutableRefObject<string | null>
}): null => {
  const { results } = useInstantSearch()
  const router = useRouter()
  const setSearchQuery = useSearchUpdate()
  const searchQuery = useSearchState()
  const analytics = useAnalytics()
  const { nbHits, setnbHits } = useSearchNBHitsContext()

  const shouldSkipRedirect = () => {
    return (
      !pendingSearchQuery ||
      pendingSearchQuery !== searchQuery ||
      searchQuery === '' ||
      lastRedirectedQuery.current === pendingSearchQuery
    )
  }

  useEffect(() => {
    if (shouldSkipRedirect()) {
      setShowLoader?.(false)
      return
    }

    const allIndexesReady = algoliaIndexes.every((indexName) =>
      nbHits.some((hit) => hit.index === indexName && hit.query === pendingSearchQuery)
    )
    const isMinimumSearchQuery = searchQuery.length <= 2

    if (
      (allIndexesReady && lastRedirectedQuery.current !== pendingSearchQuery && nbHits.length >= 2) ||
      isMinimumSearchQuery
    ) {
      // Manual Editor redirect — read userData directly from results (same response as renderingContent)
      const manualRuleRedirectUrl = getManualRuleRedirectUrl(results?.userData)
      // Visual Editor redirect — read renderingContent from results
      const visualEditorRedirectUrl = getVisualEditorRedirectUrl(results?.renderingContent as any)
      const redirectUrl = manualRuleRedirectUrl || visualEditorRedirectUrl

      const anyIndexHasHits = algoliaIndexes.some((indexName) =>
        nbHits.some((hit) => hit.index === indexName && Number(hit.nbHits) > 0)
      )
      const nbHitsRedirect =
        anyIndexHasHits || isMinimumSearchQuery
          ? `/search?query=${encodeURIComponent(pendingSearchQuery ?? '')}`
          : `/nosearch?query=${encodeURIComponent(pendingSearchQuery ?? '')}`
      const path = redirectUrl || nbHitsRedirect
      if (redirectUrl) {
        analytics.searchRedirect(redirectUrl)
      }
      lastRedirectedQuery.current = pendingSearchQuery
      router.push(path).then(() => {
        setShowLoader?.(false)
        setSearchQuery('')
        setnbHits([])
        setPendingSearchQuery(null)
      })
    }
  }, [pendingSearchQuery, searchQuery, nbHits, results, analytics, router])

  return null
}

export default Header
